import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Building2, Shield, CheckCircle2, ArrowLeft, Loader2, Lock } from "lucide-react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { submitPartnerAccessRequest } from "@/lib/partnerAccess.functions";
import { trackEvent } from "@/lib/audit";
import { BrandSlogan } from "@/components/brand/BrandSlogan";
import { requireEnterpriseAccess } from "@/lib/security/enterpriseRouteGate";

export const Route = createFileRoute("/corporate-access")({
  beforeLoad: () => requireEnterpriseAccess("/corporate-access"),
  head: () => ({
    meta: [
      { title: "Corporate Access · VerseSport — Enterprise Approval Request" },
      { name: "description", content: "Solicita acceso corporativo a VerseSport. Cada solicitud es revisada y aprobada manualmente por nuestro equipo enterprise." },
      { property: "og:title", content: "Corporate Access · VerseSport" },
      { property: "og:description", content: "Enterprise approval-based access for broadcasters, brands and sponsors." },
    ],
  }),
  component: CorporateAccessPage,
});

const Schema = z.object({
  company_name: z.string().trim().min(2).max(200),
  contact_name: z.string().trim().min(2).max(120),
  contact_email: z.string().trim().email().max(200),
  contact_phone: z.string().trim().max(40).optional().or(z.literal("")),
  country: z.string().trim().max(80).optional().or(z.literal("")),
  use_case: z.string().trim().min(10).max(1500),
  estimated_volume: z.string().trim().max(120).optional().or(z.literal("")),
});

function CorporateAccessPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitRequest = useServerFn(submitPartnerAccessRequest);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const parsed = Schema.safeParse({
      company_name: String(fd.get("company_name") ?? ""),
      contact_name: String(fd.get("contact_name") ?? ""),
      contact_email: String(fd.get("contact_email") ?? ""),
      contact_phone: String(fd.get("contact_phone") ?? ""),
      country: String(fd.get("country") ?? ""),
      use_case: String(fd.get("use_case") ?? ""),
      estimated_volume: String(fd.get("estimated_volume") ?? ""),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Revisa los datos del formulario.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitRequest({
        data: {
          company_name: parsed.data.company_name,
          contact_name: parsed.data.contact_name,
          contact_email: parsed.data.contact_email,
          contact_phone: parsed.data.contact_phone || null,
          country: parsed.data.country || null,
          use_case: parsed.data.use_case,
          estimated_volume: parsed.data.estimated_volume || null,
          source: "corporate_access",
        },
      });
      if (!result.ok) {
        console.error("[corporate-access] insert failed", result);
        void trackEvent("corporate_access_request_failed", {
          message: result.error,
          code: result.code ?? null,
        });
        throw new Error(result.error || "Insert rechazado por el backend.");
      }
      setSubmitted(true);
      void trackEvent("corporate_access_request_submitted", {
        company: parsed.data.company_name,
        country: parsed.data.country || null,
        request_id: result.id ?? null,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "No se pudo enviar la solicitud.";
      console.error("[corporate-access] submit error", err);
      setError(message);
      void trackEvent("corporate_access_request_failed", { message, stage: "catch" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <div className="border-b border-border/60 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/welcome" search={{ src: undefined, ref: undefined, tenant: undefined, lang: undefined, from: undefined }} className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver
          </Link>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            <Shield className="h-3 w-3 text-primary" /> Enterprise · Approval Required
          </span>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1.1fr_1fr]">
        <header>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-primary">
            <Building2 className="h-3.5 w-3.5" /> Corporate Access
          </div>
          <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">
            Solicita acceso enterprise a VerseSport
          </h1>
          <BrandSlogan section="partner_portal" size="md" align="left" withProduct className="mt-2" />
          <p className="mt-4 max-w-xl text-sm text-muted-foreground sm:text-base">
            El acceso corporativo no se concede automáticamente. Cada solicitud
            es revisada y aprobada manualmente por nuestro equipo. Recibirás un
            correo cuando tu cuenta esté activa, con credenciales y alcance
            asignados a tu organización.
          </p>

          <ul className="mt-6 space-y-2 text-sm">
            <Bullet>Revisión manual por SuperAdmin</Bullet>
            <Bullet>Tenant aislado con RLS multi-tenant</Bullet>
            <Bullet>Read-only por defecto, ampliable bajo acuerdo</Bullet>
            <Bullet>Auditoría completa de accesos y exports</Bullet>
          </ul>

          <div className="mt-8 rounded-2xl border border-border/60 bg-background/60 p-5 text-xs text-muted-foreground">
            ¿Ya eres partner aprobado?{" "}
            <Link to="/partners/login" className="font-bold text-primary hover:underline">
              <Lock className="-mt-0.5 mr-1 inline h-3 w-3" /> Acceder al Portal Corporativo
            </Link>
          </div>

        </header>

        <section className="rounded-2xl border border-border bg-background/80 p-5 shadow-sm sm:p-6">
          {submitted ? (
            <div className="flex flex-col items-start gap-3 py-6">
              <CheckCircle2 className="h-10 w-10 text-[oklch(0.82_0.24_145)]" />
              <h2 className="text-xl font-black">Solicitud recibida</h2>
              <p className="text-sm text-muted-foreground">
                Gracias. Nuestro equipo enterprise revisará tu solicitud y te
                contactará por correo. Las aprobaciones se procesan en horario
                comercial.
              </p>
              <Link
                to="/welcome"
                search={{ src: undefined, ref: undefined, tenant: undefined, lang: undefined, from: undefined }}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-black text-primary-foreground hover:opacity-90"
              >
                Volver al inicio
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3" noValidate>
              <h2 className="text-lg font-black">Datos de la organización</h2>
              <Field label="Empresa / Organización *" name="company_name" required maxLength={200} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nombre de contacto *" name="contact_name" required maxLength={120} />
                <Field label="Email corporativo *" name="contact_email" type="email" required maxLength={200} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Teléfono" name="contact_phone" maxLength={40} />
                <Field label="País" name="country" maxLength={80} />
              </div>
              <Field label="Volumen estimado / audiencia" name="estimated_volume" maxLength={120} />
              <div>
                <label className="mb-1 block text-xs font-bold text-muted-foreground" htmlFor="use_case">
                  Caso de uso *
                </label>
                <textarea
                  id="use_case"
                  name="use_case"
                  required
                  minLength={10}
                  maxLength={1500}
                  rows={4}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  placeholder="Broadcaster nacional, marca patrocinadora, operador comercial, etc."
                />
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-black text-primary-foreground shadow hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                {submitting ? "Enviando solicitud…" : "Enviar solicitud"}
              </button>
              <p className="text-[10px] leading-relaxed text-muted-foreground">
                Al enviar aceptas que VerseSport procese estos datos únicamente
                para evaluar tu acceso corporativo. No se crea ninguna cuenta de
                forma automática.
              </p>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}

function Field({
  label, name, type = "text", required, maxLength,
}: {
  label: string; name: string; type?: string; required?: boolean; maxLength?: number;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold text-muted-foreground" htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        maxLength={maxLength}
        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
      />
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-muted-foreground">
      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
      <span>{children}</span>
    </li>
  );
}